import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BellIcon, ChevronDownIcon, MenuIcon, SearchIcon } from 'lucide-react';
import { notifications } from '../../data/org';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useHRStore } from '../../hooks/useHRStore';
import type { Navigate } from '../../types/hr';
import { displayName } from '../../utils/employees';
import { Avatar } from '../ui/Avatar';
interface TopBarProps {
  onNavigate: Navigate;
  onOpenMenu: () => void;
}
export function TopBar({
  onNavigate,
  onOpenMenu
}: TopBarProps) {
  const {
    employees
  } = useHRStore();
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [readAll, setReadAll] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const closeBell = useCallback(() => setBellOpen(false), []);
  useClickOutside(searchRef, closeSearch, searchOpen);
  useClickOutside(bellRef, closeBell, bellOpen);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return employees.filter(e => `${displayName(e)} ${e.jobTitle} ${e.department} ${e.employeeNumber}`.toLowerCase().includes(q)).slice(0, 6);
  }, [query, employees]);
  const unread = readAll ? 0 : notifications.filter(n => n.unread).length;
  const openEmployee = (id: string) => {
    onNavigate({
      page: 'profile',
      employeeId: id
    });
    setQuery('');
    setSearchOpen(false);
  };
  return <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/85 backdrop-blur-md" data-id="element-88" data-mp="3lhg2qn9rcg26">
      <div className="flex h-16 items-center gap-4 px-6 lg:px-10" data-id="element-89" data-mp="3pj9xkex9upuf">
        <button type="button" onClick={onOpenMenu} className="rounded-lg p-2 text-navy hover:bg-slate-100 lg:hidden" aria-label="Open navigation" data-id="element-90" data-mp="3uimjmxgbjsvy">
          <MenuIcon className="h-5 w-5" data-id="element-91" data-mp="3bjsu74e7om9" />
        </button>

        <div ref={searchRef} className="relative w-full max-w-md" data-id="element-92" data-mp="8b662gk814v2">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-93" data-mp="89l8ei2m15sz" />
          <input ref={inputRef} type="search" value={query} onChange={e => {
          setQuery(e.target.value);
          setSearchOpen(true);
        }} onFocus={() => setSearchOpen(true)} onKeyDown={e => {
          if (e.key === 'Enter' && results[0]) openEmployee(results[0].id);
        }} placeholder="Search people, employee #, departments…" aria-label="Global search" className="h-10 w-full rounded-xl border-0 bg-canvas pl-10 pr-14 text-sm text-navy ring-1 ring-inset ring-slate-200/80 placeholder:text-mutedlight focus:bg-white focus:outline-none focus:ring-2 focus:ring-azure" data-id="element-94" data-mp="h3huxxzosk8x" />
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md bg-white px-1.5 py-0.5 text-[10px] font-medium text-muted ring-1 ring-slate-200 sm:block" data-id="element-95" data-mp="1jv0tqb5d03hv">
            ⌘K
          </kbd>
          <AnimatePresence data-id="element-96" data-mp="1a4fxuvh2ld2n">
            {searchOpen && query.trim() && <motion.div initial={{
            opacity: 0,
            y: -4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.12
          }} className="absolute left-0 right-0 top-12 overflow-hidden rounded-2xl bg-white py-2 shadow-lift ring-1 ring-slate-900/5" data-id="element-97" data-mp="1645tfn8xeupw">
                {results.length === 0 ? <p className="px-4 py-3 text-sm text-muted" data-id="element-98" data-mp="1nehtuhjxobu6">No people match “{query}”.</p> : <ul aria-label="Search results" data-id="element-99" data-mp="19fi75ffze70n">
                    {results.map(e => <li key={e.id} data-id="element-100" data-mp="1dh3e6k5u3r2x">
                        <button type="button" onClick={() => openEmployee(e.id)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition hover:bg-canvas" data-id="element-101" data-mp="21435f6velos6">
                          <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-102" data-mp="265itl7svobes" />
                          <span className="min-w-0 flex-1" data-id="element-103" data-mp="1sc19nlwa4wo8">
                            <span className="block truncate text-sm font-medium text-navy" data-id="element-104" data-mp="1wctgk8hoiphc">{displayName(e)}</span>
                            <span className="block truncate text-xs text-muted" data-id="element-105" data-mp="2k2nuyuhncm3p">
                              {e.jobTitle} · {e.department}
                            </span>
                          </span>
                          <span className="text-xs text-mutedlight" data-id="element-106" data-mp="ddm5wxk8l91m">{e.employeeNumber}</span>
                        </button>
                      </li>)}
                  </ul>}
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="ml-auto flex items-center gap-2" data-id="element-107" data-mp="i78as3k5s8mv">
          <div ref={bellRef} className="relative" data-id="element-108" data-mp="3qlgwqss6e162">
            <button type="button" onClick={() => setBellOpen(o => !o)} className="relative flex h-10 w-10 items-center justify-center rounded-xl text-navy transition hover:bg-canvas" aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} aria-expanded={bellOpen} data-id="element-109" data-mp="3qlyz875flbmf">
              <BellIcon className="h-5 w-5" data-id="element-110" data-mp="1fsw60l4vokwt" />
              {unread > 0 && <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-flame ring-2 ring-white" data-id="element-111" data-mp="1fvidkkrrr0f1" />}
            </button>
            <AnimatePresence data-id="element-112" data-mp="1hyal7jpxzslo">
              {bellOpen && <motion.div initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.12
            }} className="absolute right-0 top-12 w-80 overflow-hidden rounded-2xl bg-white shadow-lift ring-1 ring-slate-900/5" data-id="element-113" data-mp="1hzvivi7jzrnr">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3" data-id="element-114" data-mp="z8ukowe9c059">
                    <p className="text-sm font-semibold text-navy" data-id="element-115" data-mp="146nojis38i2d">Notifications</p>
                    <button type="button" onClick={() => setReadAll(true)} className="text-xs font-medium text-azure hover:underline" data-id="element-116" data-mp="194p8s7192nff">
                      Mark all read
                    </button>
                  </div>
                  <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto" data-id="element-117" data-mp="gepaws562am2">
                    {notifications.map(n => <li key={n.id} data-id="element-118" data-mp="2yqo61ibl5bbr">
                        <button type="button" onClick={() => {
                    setBellOpen(false);
                    onNavigate(n.route);
                  }} className="flex w-full gap-3 px-4 py-3 text-left transition hover:bg-canvas" data-id="element-119" data-mp="2ypmhqc5k6sc3">
                          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.unread && !readAll ? 'bg-flame' : 'bg-slate-200'}`} data-id="element-120" data-mp="2frjy6cvs5nlo" />
                          <span className="min-w-0" data-id="element-121" data-mp="2azacj91p79p7">
                            <span className="block text-sm font-medium text-navy" data-id="element-122" data-mp="26ygpsizzs0xw">{n.title}</span>
                            <span className="mt-0.5 block text-xs leading-relaxed text-muted" data-id="element-123" data-mp="220f4rsj09rjs">{n.body}</span>
                            <span className="mt-1 block text-[11px] text-mutedlight" data-id="element-124" data-mp="1x129qkhzde64">{n.time}</span>
                          </span>
                        </button>
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="mx-1 hidden h-8 w-px bg-slate-200 sm:block" data-id="element-125" data-mp="3kw71yx5pkqtv" />

          <button type="button" onClick={() => onNavigate({
          page: 'profile',
          employeeId: 'e1'
        })} aria-label="Open my profile" className="flex items-center gap-3 rounded-xl py-1.5 pl-1.5 pr-3 transition hover:bg-canvas" data-id="element-126" data-mp="36yijtx30zxsi">
            <Avatar name="Jordan Avery" seed="e1" size="sm" data-id="element-127" data-mp="31ye2y5y7h11r" />
            <span className="hidden text-left sm:block" data-id="element-128" data-mp="31zxl623f9m66">
              <span className="block text-sm font-medium leading-tight text-navy" data-id="element-129" data-mp="2o80ve4p87ocy">Jordan Avery</span>
              <span className="block text-[11px] leading-tight text-muted" data-id="element-130" data-mp="2t5j2dpjontib">HR Admin</span>
            </span>
            <ChevronDownIcon className="hidden h-4 w-4 text-muted sm:block" aria-hidden="true" data-id="element-131" data-mp="2ea39hnuua3ni" />
          </button>
        </div>
      </div>
    </header>;
}